import { Form, Spinner } from "react-bootstrap";
import { useLocation } from 'react-router-dom';
import React, { useState, useEffect } from "react";
import authService from "../../../utils/authService";

// CSS for dropdown scroll
const dropdownStyles = `
  .dropdown-scroll {
    max-height: 200px;
    overflow-y: auto;
  }
  
  .dropdown-scroll option {
    padding: 8px 12px;
  }
  
  .dropdown-scroll:focus {
    max-height: 200px;
    overflow-y: auto;
  }
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = dropdownStyles;
document.head.appendChild(styleSheet);

interface Batch {
  batch_name: string;
  batch_id: string;
}

interface Course {
  course_id: string;
  course_name: string;
}

interface Day {
  day: number;
  day_key: string;
  date: string;
  topics?: string[];
  practiceMCQ?: { questions?: string | null };
  practiceCoding?: { questions?: string | null };
}

interface Week {
  weekNumber: number;
  startDate: string;
  endDate: string;
  totalHours?: string;
  days?: Day[];
}

type Subject = string;

const BatchCalendarView:React.FC =()=> {
  const location = useLocation();
  const batchId = location.state?.batch_id ? location.state.batch_id : "";
  const courseId = location.state?.course_id ? location.state.course_id : "";
  const [loading, setLoading] = useState<boolean>(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState(courseId);
  const [selectedBatch, setSelectedBatch] = useState(batchId);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [subjects, setSubjects] =useState<Subject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<Subject>("");
  const [data, setData] = useState<Week[]>([]);
  const [openWeeks, setOpenWeeks] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchCourses();
  }, []);


  useEffect(() => {
    if (selectedCourse !== "") {
      fetchBatches();
    }
  }, [selectedCourse]);


  useEffect(() => {
    if(selectedCourse !=="" && selectedBatch !==""){
      fetchSubjects()
    }
  },[selectedBatch])

 useEffect(() =>{
  if(selectedCourse !=="" && selectedBatch !=="" && selectedSubject!==""){
  fetchJson();
  }
 },[selectedCourse,selectedBatch,selectedSubject])

 const fetchCourses = async () => {
  try {
    const response = await authService.get('admin/batch/get_all_courses_for_batch/');
    const fetchedCourses = response.data.courses;

    setCourses(fetchedCourses);

    if (fetchedCourses.length > 0) {
      setSelectedCourse(fetchedCourses[0].course_id); // auto-select first
    }

    setLoading(false);
  } catch (error) {
    setLoading(false);
    console.error('Error fetching courses:', error);
  }
};


  const fetchBatches = async () => {
    try {
      const response = await authService.get(`admin/batch/get_all_batches/${selectedCourse}/`);
      setBatches(response.data.batches);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const fetchSubjects = async () =>{
    try {
      const response = await authService.get(`admin/batch/get_subject_for_batch/${selectedCourse}/${selectedBatch}/`);
      setSubjects(response.data.subjects);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }

  }

  const fetchJson = async () => {
    try {
      const response = await authService.get(`admin/batch/get_batch_daywise/${selectedCourse}/${selectedBatch}/${selectedSubject}/`);
      const weeks = response.data.weeks;
      const transformedData = weeks.map((week: any) => ({
        weekNumber: week.weekNumber,
        startDate: week.startDate,
        endDate: week.endDate,
        totalHours: week.totalHours,
        topics: week.topics ? week.topics.split(',').map((topic: string) => topic.trim()) : [],
        days: week.days.map((day: any) => ({
          day: day.day,
          date: day.date,
          day_key: day.day_key,
          topics: day.topics ? day.topics.split(',').map((topic: string) => topic.trim()) : [],
          practiceMCQ: day.practiceMCQ,
          practiceCoding: day.practiceCoding,
        })),
      }));
  
      setData(transformedData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching data:', error);
    }
  };
 

  const toggleWeek = (weekNumber: number) => {
    const newOpenWeeks = new Set(openWeeks);
    if (newOpenWeeks.has(weekNumber)) {
      newOpenWeeks.delete(weekNumber);
    } else {
      newOpenWeeks.add(weekNumber);
    }
    setOpenWeeks(newOpenWeeks);
  };

  return (
    <>
      {loading &&
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Spinner animation="border" role="status" />
        </div>
      }
      <div className="border pt-3 px-2 rounded-2 bg-white my-2 me-2" style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }}>
        <div className="row mb-3 me-2">
          <div className="col-12">
                <div className="row align-items-end">
                  <div className="col-md-3">
                    <label htmlFor="courseSelect" className="form-label fw-bold">Course</label>
                    <Form.Select 
                      id="courseSelect"
                      className="dropdown-scroll"
                      onChange={(e) => { setSelectedCourse(e.target.value); setSelectedBatch('') }} 
                      value={selectedCourse}
                    >
                      <option value="">Choose course...</option>
                      {courses.map((course) => (
                        <option 
                          key={course.course_id} 
                          value={course.course_id}
                        >
                          {course.course_name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="batchSelect" className="form-label fw-bold">Batch</label>
                    <Form.Select 
                      id="batchSelect"
                      className="dropdown-scroll"
                      onChange={(e) => setSelectedBatch(e.target.value)} 
                      value={selectedBatch}
                    >
                      <option value="">Choose batch...</option>
                      {batches.map((batch) => (
                        <option 
                          key={batch.batch_id} 
                          value={batch.batch_id}
                        >
                          {batch.batch_name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="subjectSelect" className="form-label fw-bold">Subject</label>
                    <Form.Select 
                      id="subjectSelect"
                      className="dropdown-scroll"
                      onChange={(e) => setSelectedSubject(e.target.value)} 
                      value={selectedSubject}
                    >
                      <option value="">Choose subject...</option>
                      {subjects.map((subject) => (
                        <option 
                          key={subject} 
                          value={subject}
                        >
                          {subject}
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  {/* <div className="col-md-3">
                    <div className="d-flex justify-content-end">
                      <button className="btn btn-outline-secondary me-2">
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                      </button>
                    </div>
                  </div> */}
        
            </div>
          </div>
        </div>
        <div className="container-fluid bg-white mt-3" style={{ height: `calc(100vh - 200px)` , overflowY: "auto" }}>
          {data.map((week) => (
            <div className="mb-4 border border-secondary rounded p-1" key={week.weekNumber}>
              <div
                className="p-2 rounded-2 d-flex justify-content-between"
                style={{ backgroundColor: "#D7DCFF" }}
              >
                 <div className="d-flex p-0 justify-content-between flex-wrap">
                   
                    <h5 className="m-0 p-0 pe-3">Week {week.weekNumber}</h5>                 
                    <>
                      <h5 className="m-0 p-0 pe-5">{week.startDate}</h5>
                      <h5 className="m-0 p-0">{week.endDate}</h5>
                </>
                </div>
                 <div className="d-flex justify-content-between">
                   {openWeeks.has(week.weekNumber) && (
                    <>
                      {week.totalHours && (
                        <div className="d-flex align-items-center justify-content-start">
                        <h5 className="m-0 p-0 pe-2">{week.totalHours}hrs
                          </h5>
                        </div>
)}
                    </>
                  )}
                  <h5 className="m-0 p-0 pe-5">
                  </h5>

                  <button
                    className="btn btn-sm p-0 px-1 fw-bold"
                    onClick={() => toggleWeek(week.weekNumber)}
                    disabled={week.days && week.days.length === 0}
                    style={{ border: "none", outline: "none", boxShadow: "none" }}
                  >
                    {openWeeks.has(week.weekNumber) ? (
                      <span>&lt;</span>

                    ) : (
                      <span>&gt;</span>

                    )}
                  </button>
                </div>
              </div>
              {openWeeks.has(week.weekNumber) && week.days ? (
                <div className="">        
                    <div className="py-1 d-flex justify-content-between align-items-center" >
                      <div className=" me-3" style={{padding: "0px 0px 0px 60px"}}>
                      </div>
                      <div className="d-flex justify-content-between flex-grow-1 ">
                      <div className="d-flex align-items-center" style={{width:'70px'}}></div>                        
                      <div
                            className="text-start d-flex align-items-center"
                            role="button"
                            style={{
                              fontSize: "14px",
                              width:
                                window.innerWidth >= 1280
                                  ? "500px"
                                  : window.innerWidth >= 1024
                                  ? "300px"
                                  : window.innerWidth >= 768
                                  ? "200px"
                                  : "120px",
                            }}
                          >
                          </div>
                      <div className="" style={{fontSize:'15px',color:'#000', paddingLeft:'75px'}}>Practice MCQ </div>
                      <div className="" style={{fontSize:'15px',color:'#000',paddingRight:"10px"  }}>Practice Coding </div>
                      </div>
                    </div>


                  {week.days.map((day) => (
                    <div
                      className="mb-2 d-flex justify-content-between align-items-center"
                      key={day.day}
                    >
                      <div className="border-end border-1 border-black me-3" style={{padding: "10px"}}>
                        Day {day.day}
                      </div>
                      <div className="d-flex justify-content-between flex-grow-1 border border-2 rounded-2 p-1">
                        {day.date && <div className="d-flex align-items-center">{day.date}</div>}
                        {day.topics && (
                          <div
                            className="text-start d-flex align-items-center"
                            role="button"
                            title={
                              day.topics
                                ? day.topics
                                    .map((topic) => `${topic}`)
                                    .join(" | ")
                                : ""
                            }
                            style={{
                              fontSize: "14px",
                              width:
                                window.innerWidth >= 1280
                                  ? "500px"
                                  : window.innerWidth >= 1024
                                  ? "300px"
                                  : window.innerWidth >= 768
                                  ? "200px"
                                  : "120px",
                            }}
                          >
                            {day.topics
                              ? day.topics
                                  .map((topic) => `${topic}`)
                                  .join(" | ")
                              : ""}
                          </div>
                        )}
                        {(day.practiceMCQ || day.practiceCoding) ? (
                          <>
                            <div style={{fontSize: "12px"}}>
                              {day.practiceMCQ && (
                                <>
                                  {day.practiceMCQ.questions && (
                                    <p className="m-0 d-flex justify-content-end">
                                      Questions: {day.practiceMCQ.questions}
                                    </p>
                                  )}
                                  {/* {day.practiceMCQ.score && (
                                    <p className="m-0 d-flex justify-content-end">
                                      Score: {day.practiceMCQ.score}
                                    </p>
                                  )} */}
                                </>
                              )}
                            </div>
                            <div style={{fontSize: "12px"}}>
                              {day.practiceCoding && (
                                <>
                                  {day.practiceCoding.questions && (
                                    <p className="m-0 d-flex justify-content-end" style={{paddingRight:"10px"}}>
                                      Questions: {day.practiceCoding.questions}
                                    </p>
                                  )}
                                  {/* {day.practiceCoding.score && (
                                    <p className="m-0 d-flex justify-content-end">
                                      Score: {day.practiceCoding.score}
                                    </p>
                                  )} */}
                                </>
                              )}
                            </div>
                          </>
                        ):(
                          <>
                          <div style={{fontSize: "12px"}}>
                            {!day.practiceMCQ && (
                              <>
                                  <p className="m-0 d-flex justify-content-end">
                                    <span className="px-3"></span>
                                  </p>
                                  <p className="m-0 d-flex justify-content-end">
                                    <span className="px-3"></span>
                                  </p>
                              </>
                            )}
                          </div>
                        </>
                        )}

                        {/* {day.testScore && day.testScore.score && (
                          <div className="py-2 ps-5" style={{fontSize:'14px'}}>Score : {day.testScore.score}</div>
                        )}
                        {day.status && (
                          <div>
                            <button
                              style={{
                                width: "85px",
                                height: "30px",
                                backgroundColor: "#E9EBFF",
                                boxShadow: "0px 6px 6px rgba(0, 0, 0, 0.3)",
                              }}
                              className="btn btn-sm"
                              onClick={() => handleStartButtonClick(day.day_key, week.weekNumber)}
                            >
                              {day.status}
                            </button>
                          </div>
                        )} */}

                        {/* {!day.status && <div style={{ width: "85px" }}></div>} */}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>

      </div>
    </>
  )

}

export default BatchCalendarView